import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { FaPrint } from 'react-icons/fa';
import logo from '../../assets/OG-logo-main-learning-scores.png';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Disable React DevTools in production
if (process.env.NODE_ENV === 'production' && typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object') {
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function () {};
}

const SubjectScoresPage = ({ userId }) => {
  const [partIIScores, setPartIIScores] = useState([]);
  const [partIIIScores, setPartIIIScores] = useState([]);
  const [showPart3Content, setShowPart3Content] = useState(false); // New state for Part III eligibility
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
const viewingChildId = location.state?.viewingChildId || queryParams.get('viewingChildId') || null;
const viewingChildName = location.state?.childName || decodeURIComponent(queryParams.get('childName') || '') || null;
  const chartRefs = useRef({});
  const pageRef = useRef(null);

  useEffect(() => {
  setIsMounted(true);
  const currentChartRefs = chartRefs.current; // Capture the current ref value

  // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Subject Scores Page',
      });
    }

  return () => {
    setIsMounted(false);
    Object.values(currentChartRefs).forEach((chart) => chart?.destroy()); // Use captured value
  };
}, [location]);

// Remove console.logs in production
  if (process.env.NODE_ENV !== "development") {
    console.log = () => {};
  }
  
  // Process scores and check Part III eligibility
  useEffect(() => {
    const processScores = () => {
      if (!userId) {
        if (isMounted) {
          setError('No user ID provided');
          setLoading(false);
        }
        return;
      }

      // Access scores from navigation state
      const { partIIScores = [], partIIIScores = [] } = location.state || {};

      if (partIIScores.length === 0 && partIIIScores.length === 0) {
        if (isMounted) {
          setError('No scores provided. Please navigate from the dashboard.');
          setLoading(false);
        }
        return;
      }

      try {
        // Set scores
        if (isMounted) {
          setPartIIScores(partIIScores);
          setPartIIIScores(partIIIScores);
        }

        // Check Part III eligibility based on top 2 facets
        if (partIIScores.length > 0) {
          const sortedScores = [...partIIScores].sort(
            (a, b) => (b.averageScore || 0) - (a.averageScore || 0)
          );

          let topFacets = [sortedScores[0]?._id];
          if (sortedScores.length > 1) {
            const secondHighestScore = sortedScores[1].averageScore;
            topFacets = topFacets.concat(
              sortedScores
                .slice(1)
                .filter((score) => score.averageScore >= secondHighestScore)
                .map((score) => score._id)
            );
          }

          const hasPartIIITaxonomy = topFacets.some((facet) => {
            const facetScore = partIIScores.find((score) => score._id === facet);
            if (!facetScore?.taxonomy) {
              console.warn(`No taxonomy for facet ${facet}`, facetScore);
              return false;
            }
            const taxonomy = Array.isArray(facetScore.taxonomy)
              ? facetScore.taxonomy[0]
              : facetScore.taxonomy;
            return taxonomy === 'Part III';
          });

          console.log('Part III eligibility check:', {
            topFacets,
            hasPartIIITaxonomy,
          });

          if (isMounted) {
            setShowPart3Content(hasPartIIITaxonomy);
          }
        } else {
          if (isMounted) {
            setShowPart3Content(false);
          }
        }

        if (isMounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error processing scores:', error);
        if (isMounted) {
          setError('Failed to process scores. Please try again.');
          setLoading(false);
        }
      }
    };

    processScores();
  }, [userId, location.state, isMounted]);

   const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
  const handleResize = () => {
    // Trigger re-render so chartOptions reads new window.innerWidth
    setPartIIScores([...partIIScores]);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [partIIScores]);


    useEffect(() => {
      document.body.classList.add('specific-page-uniready');
      return () => document.body.classList.remove('specific-page-uniready');
    });
    
  // Memoized chart options
const chartOptions = useMemo(() => {
  const isMobile = window.innerWidth <= 850;

  return (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: title,
        align: 'center',
        font: { size: isMobile ? 16 : 21, weight: 'bold' },
        color: '#028be8',
        padding: { top: 20, bottom: 30 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          color: 'white',
          font: { size: isMobile ? 12 : 16 },
          stepSize: 1,
          callback: (value) => (Number.isInteger(value) ? value : null),
        },
      },
      x: {
        ticks: {
          color: 'white',
          font: { size: isMobile ? 10 : 16 },   // much smaller on mobile
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  });
}, []); // Empty dependency → will read current window width each time it's called

  const renderScoreTable = (scores, title) => {
    if (scores.length === 0) {
      return (
        <p style={{ color: 'white', fontSize: '14px', textAlign: 'center', margin: '20px 0' }}>
          No scores available for this part.
        </p>
      );
    }

    
    return (
      <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto', padding: '20px 0' }}>
        <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '10px', textAlign: 'center' }}>
          {title}
        </h3>
        <table
          className="scores-table"
          style={{
            width: '100%',
            maxWidth: '800px',
            textAlign: 'left',
            color: 'white',
            fontSize: '14px',
            borderCollapse: 'collapse',
            borderRadius: '10px',
            background: 'linear-gradient(to right, #0b1021, rgb(30, 37, 58))',
            margin: '20px 0 40px',
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '15px' }}>Subject</th>
              <th style={{ textAlign: 'right', padding: '15px' }}>Average Score</th>
              <th style={{ textAlign: 'right', padding: '15px' }}>Total Score</th>
            </tr>
          </thead>
          <tbody>
            {scores.map((score) => (
              <React.Fragment key={score._id || Math.random()}>
                <tr>
                     <td style={{ textAlign: 'left', padding: '10px 15px' }}>
                  <a
                    href={`https://www.bachelorsportal.com/search/bachelor?kw-what=${encodeURIComponent(score._id || 'Unknown')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#36a7f8',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {score._id || 'Unknown'}
                  </a>
                </td>
                  <td style={{ textAlign: 'right', padding: '10px 15px' }}>
                    {score.averageScore ? score.averageScore.toFixed(1) : 'N/A'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px 15px' }}>
                    {score.totalScore || 0}
                  </td>
                </tr>
                {score.facetNote?.trim() && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{ textAlign: 'left', padding: '0 15px 10px', color: '#ffffffcc', fontSize: '12px' }}
                    >
                      {score.facetNote}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChart = (key, title, chartData, tableTitle, scores) => {
    return (
      <div
        className="chart-section"
        style={{
          margin: '50px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div
          className="chart-container-subject"
          style={{ height: '500px', width: '800px', margin: '0 auto' }}
        >
          <Bar
            ref={(el) => {
              if (el) chartRefs.current[key] = el.chart;
              else delete chartRefs.current[key];
            }}
            data={chartData}
            options={chartOptions(title)}
          />
        </div>
        {renderScoreTable(scores, tableTitle)}
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  // Sort scores for Part II and Part III
  const sortedPartIIScores = [...partIIScores].sort(
    (a, b) => (b.averageScore || 0) - (a.averageScore || 0)
  );
  const sortedPartIIIScores = [...partIIIScores].sort(
    (a, b) => (b.averageScore || 0) - (a.averageScore || 0)
  );

  // Chart data for Part II
  const partIIChartData = {
    labels: sortedPartIIScores.map((score) => score._id || 'Unknown'),
    datasets: [
      {
        label: 'Average Score',
        data: sortedPartIIScores.map((score) => score.averageScore || 0),
        facetNote: sortedPartIIScores.map((score) => score.facetNote || null),
        backgroundColor: 'rgba(75, 192, 192, 0.29)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Chart data for Part III
  const partIIIChartData = {
    labels: sortedPartIIIScores.map((score) => score._id || 'Unknown'),
    datasets: [
      {
        label: 'Average Score',
        data: sortedPartIIIScores.map((score) => score.averageScore || 0),
        facetNote: sortedPartIIIScores.map((score) => score.facetNote || null),
        backgroundColor: 'rgba(153, 102, 255, 0.29)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
      },
    ],
  };



  return (
    <div className='subjectscores'>
    <div ref={pageRef} style={{ padding: '0', maxWidth: '2000px', margin: '0 auto' }}>
      <header className="assessment-header-scores">
        <img src={logo} className="logo-adjustment-score" alt="StudyStyle Logo" />
        <h1 className="score-heading">UniReady Subject Scores</h1>
        <h2 style={{ color: 'white', fontSize: '12px', marginBottom: '20px' }}>
          Higher Education Subject Preferences
        </h2>
        <div className="header-buttons">
          <button
            className="return-dashboard-cross"
onClick={() => {
  const params = viewingChildId
    ? `?viewingChildId=${encodeURIComponent(viewingChildId)}&childName=${encodeURIComponent(viewingChildName)}`
    : '';
  navigate(`/dashboard${params}`, {
    state: {
      fromSubjectScores: true,
      ...(viewingChildId && { viewingChildId, childName: viewingChildName }),
    },
  });
}}
            title="Return to Dashboard"
          ></button>
          <button
            className="print-button"
            onClick={handlePrint}
            title="Print as PDF"
            disabled={loading}
          >
            <FaPrint size={35} />
          </button>
        </div>
      </header>
      <div className="content-container">
        {renderChart(
          'partII',
          'UniReady Subject Scores',
          partIIChartData,
          '',
          sortedPartIIScores
        )}
        {showPart3Content && partIIIScores.length > 0 ? (
          renderChart(
            'partIII',
            'Advanced Subject Exploration Scores',
            partIIIChartData,
            '',
            sortedPartIIIScores
          )
        ) : (
          <p style={{ color: 'white', fontSize: '14px', textAlign: 'center', margin: '20px 0' }}>
            {showPart3Content
              ? 'No Advanced Subject Exploration scores available.'
              : ''}
          </p>
        )}
      </div>
    </div>
    </div>
  );
};

export default SubjectScoresPage;